import {createSelector} from 'reselect';

import * as yapRunCommandActionTypes from '../../consts/yap/yapRunCommandActionTypes';
import {selectIsHolderConnected, selectIsIotDeviceDelta} from './iotDevice';

const selectState = (state) => state.yapEncrypted;

const makeSelectAsset = (deviceSerialNumber) =>
    createSelector(selectState, (state) => state.yapAssets?.find((asset) => asset.serialNumber === deviceSerialNumber));

const makeSelectAssetByAssetId = (assetId) =>
    createSelector(selectState, (state) => state.yapAssets?.find((asset) => asset.assetId === assetId));

const makeSelectAssetByType = (isHolder = false) =>
    createSelector(selectState, (state) => state.yapAssets?.find((asset) => asset.isHolder === isHolder));

const selectDeviceAsset = makeSelectAssetByType();
const selectHolderAsset = makeSelectAssetByType(true);

const makeSelectAssetIdByType = (isHolder = false) =>
    createSelector(selectState, (state) => state.yapAssets?.find((asset) => asset.isHolder === isHolder)?.assetId);

const makeSelectIsYapActivationInProgress = () =>
    createSelector(selectState, (state) => state.isYapActivationInProgress);

const makeSelectIsYapSynchronizeDataCollectionInProgress = () =>
    createSelector(selectState, (state) => state.isYapSynchronizeDataCollectionInProgress);

const makeSelectYapSynchronizeDataCollectionData = () =>
    createSelector(selectState, (state) => {
        return {
            isYapSynchronizeDataCollectionInProgress: state.isYapSynchronizeDataCollectionInProgress,
            isYapSynchronizeDataCollectionIsHolder: state.isYapSynchronizeDataCollectionIsHolder,
        };
    });

const makeSelectIsYapDataCollectionAllowed = () =>
    createSelector(selectState, (state) => state.isYapDataCollectionAllowed);

const makeSelectIsYapSynchronizeDataCollectionReceived = (isHolder = false) =>
    createSelector(selectState, (state) =>
        isHolder
            ? state.isYapSynchronizeHolderDataCollectionReceived
            : state.isYapSynchronizeDeviceDataCollectionReceived
    );

const makeSelectIsYapDeviceInfoReceived = () => createSelector(selectState, (state) => state.isYapDeviceInfoReceived);

const makeSelectIsYapHolderInfoReceived = () => createSelector(selectState, (state) => state.isYapHolderInfoReceived);

const makeSelectIsYapInfoReceived = () =>
    createSelector(
        selectIsHolderConnected,
        makeSelectIsYapDeviceInfoReceived(),
        makeSelectIsYapHolderInfoReceived(),
        (isHolderConnected, isYapDeviceInfoReceived, isYapHolderInfoReceived) =>
            isHolderConnected ? isYapHolderInfoReceived && isYapDeviceInfoReceived : isYapDeviceInfoReceived
    );

const makeSelectIsYapLeviaTargetInfoReceived = () =>
    createSelector(
        selectIsIotDeviceDelta,
        makeSelectIsYapDeviceInfoReceived(),
        makeSelectIsYapHolderInfoReceived(),
        (isDelta, isYapDeviceInfoReceived, isYapHolderInfoReceived) =>
            isDelta ? isYapDeviceInfoReceived : isYapHolderInfoReceived
    );

const makeSelectIsYapAutoCleaningInProgress = () =>
    createSelector(selectState, (state) => state.isYapAutoCleaningInProgress);

const makeSelectIsYapResetInProgress = () =>
    createSelector(
        makeSelectIsYapUpdateSettingsInProgress(),
        makeSelectRequestActionTypeData(),
        makeSelectRequestActionTypeData(true),
        (isYapUpdateSettingsInProgress, requestActionTypeData, holderRequestActionTypeData) => {
            if (!isYapUpdateSettingsInProgress) return false;

            const {actionType} = requestActionTypeData || {};
            const {actionType: holderActionType} = holderRequestActionTypeData || {};

            return (
                actionType === yapRunCommandActionTypes.RESET_DEVICE ||
                holderActionType === yapRunCommandActionTypes.RESET_DEVICE
            );
        }
    );

const makeSelectRequestActionTypeData = (isHolder) =>
    createSelector(selectState, (state) =>
        isHolder ? state.yapHolderRequestActionTypeData : state.yapRequestActionTypeData
    );

const makeSelectIsYapFindDeviceInProgress = () =>
    createSelector(selectState, (state) => state.isYapFindDeviceInProgress);

const makeSelectIsYapUpdateSettingsInProgress = (isHolder = false) =>
    createSelector(selectState, (state) =>
        isHolder ? state.isYapHolderUpdateSettingsInProgress : state.isYapUpdateSettingsInProgress
    );

const selectIsYapUpdateSettingsInProgress = createSelector(
    selectState,
    (state) => state.isYapHolderUpdateSettingsInProgress || state.isYapUpdateSettingsInProgress
);

const makeSelectYapCancelDataCollectionInProgress = () =>
    createSelector(selectState, (state) => state.isCancelDataCollectionInProgress);

const makeSelectIsYapFirmwareInProgress = () => createSelector(selectState, (state) => state.isYapFirmwareInProgress);

const makeSelectIsOfflineYapFirmwareInProgress = () =>
    createSelector(selectState, (state) => state.isOfflineYapFirmwareInProgress);

const makeSelectIsDeviceSyncInProgress = (isHolder) =>
    createSelector(selectState, (state) => (isHolder ? state.isYapHolderSyncInProgress : state.isYapSyncInProgress));

const makeSelectIsSocketAlive = (isHolder) =>
    createSelector(selectState, (state) => (isHolder ? state.isYapHolderSoketAlive : state.isYapSoketAlive));

const makeSelectIsYapDeviceConnected = () => createSelector(selectState, (state) => state.isYapDeviceConnected);

const makeSelectIsYapHolderConnected = () => createSelector(selectState, (state) => state.isYapHolderConnected);

const makeSelectYapLastRequestId = (isHolder = false) =>
    createSelector(selectState, (state) => (isHolder ? state.lastHolderRequestId : state.lastRequestId));

const makeSelectDataCollectionLastRequestId = (isHolder = false) =>
    createSelector(selectState, (state) =>
        isHolder ? state.lastHolderDataCollectionRequestId : state.lastDataCollectionRequestId
    );

const makeSelectSyncLastRequestId = (isHolder) =>
    createSelector(selectState, (state) => (isHolder ? state.lastHolderSyncRequestId : state.lastSyncRequestId));

const makeSelectIsYapForceCheckRequest = (isHolder) =>
    createSelector(selectState, (state) =>
        isHolder ? state.isYapHolderForceCheckRequest : state.isYapForceCheckRequest
    );

const makeSelectCmMessageData = () =>
    createSelector(selectState, (state) => {
        return {cmToken: state.cmToken, cmRequestId: state.cmRequestId};
    });

const makeSelectIsHolderByAssetId = (assetId) =>
    createSelector(makeSelectAssetByAssetId(assetId), (asset) => {
        const {isHolder} = asset || {};

        return isHolder;
    });

const makeSelectIsYapUsageFlow = () => createSelector(selectState, (state) => state.isYapUsageFlow);

const makeSelectIsYapUsageFlowDataCollectionInProgress = () =>
    createSelector(selectState, (state) => state.isYapUsageFlowDataCollectionInProgress);

const makeSelectIsYapUsageFlowDataCollectionReceived = (isHolder = false) =>
    createSelector(selectState, (state) =>
        isHolder ? state.isYapUsageFlowHolderDataCollectionReceived : state.isYapUsageFlowDeviceDataCollectionReceived
    );

const makeSelectYapLastDataCollectionTime = () =>
    createSelector(selectState, (state) => state.lastDataCollectionTimes?.find((time) => time?.isValid)?.time);

export {
    makeSelectAsset,
    makeSelectAssetByAssetId,
    makeSelectAssetByType,
    makeSelectAssetIdByType,
    makeSelectCmMessageData,
    makeSelectDataCollectionLastRequestId,
    makeSelectIsDeviceSyncInProgress,
    makeSelectIsHolderByAssetId,
    makeSelectIsOfflineYapFirmwareInProgress,
    makeSelectIsSocketAlive,
    makeSelectIsYapActivationInProgress,
    makeSelectIsYapAutoCleaningInProgress,
    makeSelectIsYapDataCollectionAllowed,
    makeSelectIsYapDeviceConnected,
    makeSelectIsYapDeviceInfoReceived,
    makeSelectIsYapFindDeviceInProgress,
    makeSelectIsYapFirmwareInProgress,
    makeSelectIsYapForceCheckRequest,
    makeSelectIsYapHolderConnected,
    makeSelectIsYapHolderInfoReceived,
    makeSelectIsYapInfoReceived,
    makeSelectIsYapLeviaTargetInfoReceived,
    makeSelectIsYapResetInProgress,
    makeSelectIsYapSynchronizeDataCollectionInProgress,
    makeSelectIsYapSynchronizeDataCollectionReceived,
    makeSelectIsYapUpdateSettingsInProgress,
    makeSelectIsYapUsageFlow,
    makeSelectIsYapUsageFlowDataCollectionInProgress,
    makeSelectIsYapUsageFlowDataCollectionReceived,
    makeSelectRequestActionTypeData,
    makeSelectSyncLastRequestId,
    makeSelectYapCancelDataCollectionInProgress,
    makeSelectYapLastDataCollectionTime,
    makeSelectYapLastRequestId,
    makeSelectYapSynchronizeDataCollectionData,
    selectDeviceAsset,
    selectHolderAsset,
    selectIsYapUpdateSettingsInProgress,
};
